import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Coins, ArrowDownLeft, ArrowUpRight, TrendingUp, TrendingDown,
    RefreshCw, Users, Percent,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Tile = ({ label, period, testId }) => (
    <div className="rounded-lg border border-white/10 bg-[#0e0e0e] p-3" data-testid={testId}>
        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">{label}</p>
        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <span className="text-[#8fbea4] flex items-center gap-1"><ArrowDownLeft className="w-3 h-3 text-[#39FF14]" />IN</span>
            <span className="font-mono text-[#39FF14] text-right">{fmt(period.wagered)}</span>
            <span className="text-[#8fbea4] flex items-center gap-1"><ArrowUpRight className="w-3 h-3 text-[#FF3B30]" />OUT</span>
            <span className="font-mono text-[#FF3B30] text-right">{fmt(period.paid_out)}</span>
            <span className="text-[#8fbea4] flex items-center gap-1"><Coins className="w-3 h-3 text-[#FFD700]" />House net</span>
            <span className={`font-mono text-right font-black ${period.house_net >= 0 ? 'text-[#FFD700]' : 'text-[#FF3B30]'}`}>
                {period.house_net >= 0 ? '+' : ''}{fmt(period.house_net)}
            </span>
            <span className="text-[#8fbea4] flex items-center gap-1"><Percent className="w-3 h-3" />Hold</span>
            <span className="font-mono text-white text-right">{(period.hold_pct || 0).toFixed(2)}%</span>
            <span className="text-[#8fbea4]">Rounds</span>
            <span className="font-mono text-white text-right">{period.rounds}</span>
            <span className="text-[#8fbea4] flex items-center gap-1"><Users className="w-3 h-3" />Players</span>
            <span className="font-mono text-white text-right">{period.unique_players}</span>
        </div>
    </div>
);

// Color chip that mirrors the roulette wheel pocket color — helps the eye
// spot patterns in the recent-rounds list.
const PocketChip = ({ pocket, color }) => {
    const bg = color === 'red' ? 'bg-[#c11b1b]' : color === 'black' ? 'bg-[#111] border border-white/20' : 'bg-[#39FF14]';
    const fg = color === 'green' ? 'text-black' : 'text-white';
    return (
        <span className={`inline-flex items-center justify-center min-w-[24px] h-6 rounded-full text-[10px] font-black ${bg} ${fg}`}>
            {pocket ?? '—'}
        </span>
    );
};

/**
 * RouletteReportCard — admin financial report for the in-house Roulette
 * wheel. Aggregates from `roulette_wager` / `roulette_payout` transactions
 * and `roulette_games`, mirroring the Blackjack report shape.
 */
export default function RouletteReportCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/reports/roulette`, { withCredentials: true });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Roulette report');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!data) {
        return (
            <div className="card-dark p-5 text-[#A0AAB2] text-xs" data-testid="roulette-report-loading">
                Loading Roulette report…
            </div>
        );
    }

    return (
        <div className="card-dark p-5 space-y-4" data-testid="roulette-report-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-[#c11b1b]" />
                    <h3 className="text-sm uppercase font-bold tracking-wide text-white">Roulette — Ins &amp; Outs</h3>
                    <span className="text-[10px] text-[#A0AAB2]">house wheel · realtime</span>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1"
                    data-testid="roulette-report-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Tile label="Today (CT)" period={data.today} testId="roulette-report-today" />
                <Tile label="This week" period={data.week} testId="roulette-report-week" />
                <Tile label="This month" period={data.month} testId="roulette-report-month" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2">Recent spins</p>
                    <div className="rounded-lg border border-white/5 bg-[#0a0a0a] max-h-64 overflow-y-auto divide-y divide-white/5" data-testid="roulette-report-recent">
                        {data.recent_rounds.length === 0 ? (
                            <p className="text-[11px] text-[#A0AAB2] italic p-3">No completed spins yet.</p>
                        ) : data.recent_rounds.map((r) => (
                            <div key={r.id} className="flex items-center justify-between px-3 py-2 text-[11px]" data-testid={`roulette-report-row-${r.id}`}>
                                <div className="min-w-0 flex items-center gap-2">
                                    <PocketChip pocket={r.winning_pocket} color={r.winning_color} />
                                    <div>
                                        <p className="text-white truncate">{r.name}</p>
                                        <p className="text-[#A0AAB2]">
                                            {new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-mono text-[#A0AAB2]">bet {fmt(r.total_bet)}</p>
                                    <p className={`font-mono font-bold ${r.net > 0 ? 'text-[#39FF14]' : r.net < 0 ? 'text-[#FF3B30]' : 'text-[#FFD700]'}`}>
                                        {r.net >= 0 ? '+' : ''}{fmt(r.net)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3 text-[#39FF14]" />
                            Top winners (30d)
                        </p>
                        <div className="rounded-lg border border-white/5 bg-[#0a0a0a] divide-y divide-white/5" data-testid="roulette-report-winners">
                            {data.top_winners_30d.length === 0 ? (
                                <p className="text-[11px] text-[#A0AAB2] italic p-3">No spins in last 30 days.</p>
                            ) : data.top_winners_30d.slice(0, 5).map((w) => (
                                <div key={w.user_id} className="flex items-center justify-between px-3 py-2 text-[11px]">
                                    <span className="text-white truncate">{w.name}</span>
                                    <span className="flex items-center gap-2">
                                        <span className="text-[#A0AAB2] font-mono">{w.rounds}s</span>
                                        <span className={`font-mono font-bold ${w.net > 0 ? 'text-[#39FF14]' : 'text-[#A0AAB2]'}`}>{w.net >= 0 ? '+' : ''}{fmt(w.net)}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2 flex items-center gap-1">
                            <TrendingDown className="w-3 h-3 text-[#FF3B30]" />
                            Top losers (30d) — house wins from
                        </p>
                        <div className="rounded-lg border border-white/5 bg-[#0a0a0a] divide-y divide-white/5" data-testid="roulette-report-losers">
                            {data.top_losers_30d.length === 0 ? (
                                <p className="text-[11px] text-[#A0AAB2] italic p-3">No spins in last 30 days.</p>
                            ) : data.top_losers_30d.slice(0, 5).map((w) => (
                                <div key={w.user_id} className="flex items-center justify-between px-3 py-2 text-[11px]">
                                    <span className="text-white truncate">{w.name}</span>
                                    <span className="flex items-center gap-2">
                                        <span className="text-[#A0AAB2] font-mono">{w.rounds}s</span>
                                        <span className={`font-mono font-bold ${w.net < 0 ? 'text-[#FF3B30]' : 'text-[#A0AAB2]'}`}>{fmt(w.net)}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
